import {
  AUTH_INIT_MODELS,
  type OldShortLink,
  SDKApiKeyResponse,
  type ShortLink,
} from '@guestapp/sdk';
import {MAIN_TOKEN_ID_KEY, RESERVATION_ID_KEY} from 'utils/constants';
import ChekinProSettings from 'ChekinProSettings';
import {environments} from 'configs';
import {LocalStorage} from '@guestapp/core';
import {SDK_MODE} from 'utils/embedded';

const IS_SDK_MODE = environments.VITE_APP_IS_SDK;

const isSDKApiKeyResponse = (
  data?: OldShortLink | ShortLink | SDKApiKeyResponse,
): data is SDKApiKeyResponse =>
  Boolean(
    (data as SDKApiKeyResponse)?.access_token ||
      (data as SDKApiKeyResponse)?.branding_template,
  );

const getReservationFromStorage = () => {
  if (IS_SDK_MODE) {
    const idFromStorage = LocalStorage.get(RESERVATION_ID_KEY) ?? '';
    return ChekinProSettings.reservationId ?? idFromStorage;
  } else {
    const currentReservationId = LocalStorage.get(RESERVATION_ID_KEY);
    const storedMainTokenIdKey = LocalStorage.get(MAIN_TOKEN_ID_KEY);
    const [, , , token] = window.location.href.split('/');
    const isIdRelevant = storedMainTokenIdKey === token;

    if (isIdRelevant) {
      return (
        currentReservationId?.toString() ?? import.meta.env.VITE_APP_TEST_RESERVATION_ID
      );
    }
  }
};

function isNewShortLink(
  data?: OldShortLink | ShortLink | SDKApiKeyResponse,
): data is ShortLink {
  return Boolean((data as ShortLink)?.instance_id);
}

const isShortLink = (
  data?: OldShortLink | ShortLink | SDKApiKeyResponse,
): data is OldShortLink | ShortLink =>
  Boolean(
    (data as OldShortLink)?.type ||
      (data as OldShortLink)?.object_id ||
      (data as ShortLink)?.instance_id,
  );

const getResourceType = () => {
  if (!IS_SDK_MODE) {
    return;
  }

  if (ChekinProSettings.reservationId || ChekinProSettings.externalReservationId) {
    return 'RESERVATION';
  }

  if (ChekinProSettings.housingId || ChekinProSettings.externalReservationId) {
    return 'HOUSING';
  }
};

const getObjectDetails = (data?: OldShortLink | ShortLink | SDKApiKeyResponse) => {
  if (IS_SDK_MODE) {
    return {
      id: ChekinProSettings.housingId || ChekinProSettings.externalHousingId,
      model:
        ChekinProSettings.mode === SDK_MODE.propertyLink
          ? AUTH_INIT_MODELS.housing
          : undefined,
      link: '',
      redirectTo: '',
      searchMode: '',
    };
  }

  if (isShortLink(data) && !isNewShortLink(data)) {
    return {
      id: data?.object_id,
      model: data?.model_name,
      link: data?.link,
      redirectTo: data?.url_path,
      searchMode: data?.reservation_search_mode,
    };
  }

  return {
    id: '',
    model: '',
    link: '',
    redirectTo: '',
    searchMode: '',
  };
};

const getTemplateId = (data?: SDKApiKeyResponse | OldShortLink | ShortLink) => {
  if (isShortLink(data) || isSDKApiKeyResponse(data)) {
    return data?.branding_template;
  }
};

const getCustomFormVersion = (data?: SDKApiKeyResponse | OldShortLink | ShortLink) => {
  if (isShortLink(data) || isSDKApiKeyResponse(data)) {
    return data.custom_forms_version || 'V1';
  } else {
    return 'V1';
  }
};

export {
  getReservationFromStorage,
  getObjectDetails,
  isSDKApiKeyResponse,
  getResourceType,
  getTemplateId,
  isShortLink,
  getCustomFormVersion,
  isNewShortLink,
};
